import React, { useState, useCallback, useMemo } from 'react';
import { TVisningstyp } from '../BusinessCollectionComponents/BusinessDataSet/BusinessDataSet';
import { clone } from 'ramda';
import {
  AttributePredicateType,
  AssociationPredicateType,
} from '../../../../../Model/avenyModelUtils';
import { rutinPredicates } from './Utils/Predicates.utils';
import { IUIUrval } from '../../../../../Types/UrvalTypes';
import { rutinInfos, rutinKeys } from '../../Const/RutinInfo';
import { IRutinInfo } from '../../../../../Types/RutinInfoType';

interface IProviderProps {
  activeRoutine?: IActiveRoutine;
  currentBookingGroup?: string;
}

export interface IRutinFieldsPredicate {
  attributePredicate: AttributePredicateType;
  associationPredicate: AssociationPredicateType;
}

const defaultUrvalAttributePredicate: AttributePredicateType = () => {
  return true;
};

const defaultUrvalAssociationPredicate: AssociationPredicateType = () => {
  return true;
};

export const defaultUrvalFieldsPredicates: IRutinFieldsPredicate = {
  attributePredicate: defaultUrvalAttributePredicate,
  associationPredicate: defaultUrvalAssociationPredicate,
};

export interface IRoutineAvailabilityInfo extends IRutinInfo {
  readonly: boolean;
  visningsTyper: IVisningsTyper;
  detaljurval: IDetaljurvalList;
}
export interface IActiveRoutine {
  routine: IRoutineAvailabilityInfo;
  predicates: IRutinFieldsPredicate;
}

interface IVisningsTyper {
  [business: string]: TVisningstyp;
}

interface IDetaljurvalList {
  [business: string]: IUIUrval | undefined;
}
export interface IRoutineSetting {
  visningsTyper?: IVisningsTyper;
  detaljurval?: IDetaljurvalList;
  currentBookingGroup?: string;
}
export interface IRoutineSettings {
  [rutinId: number]: IRoutineSetting | undefined;
}
export interface IRutinSettingsContext extends IProviderProps {
  settings: IRoutineSettings;
  /** Aktiv rutin, använd ActiveRoutineContext för komponenter inne i rutinen */
  activeRoutine?: IActiveRoutine;
  setActiveRoutine: (routine?: number) => void;
  setVisningsTyp: (
    rutin: number,
    collectionName: string
  ) => (typ: TVisningstyp) => void;
  setDetaljurval: (
    rutin: number,
    collectionName: string
  ) => (detaljurval: IUIUrval | null) => void;
  rutinStartErrorDialogOpen: boolean;
  setRutinStartErrorDialogOpen: (open: boolean) => void;
  hasRutinStartError: boolean;
  setHasRutinStartError: (value: boolean) => void;
  setCurrentBookingGroup: (bookingGroup: string) => void;
}

export const RutinSettingsContext = React.createContext<IRutinSettingsContext>({
  settings: {},
  setVisningsTyp: () => () => {},
  setDetaljurval: () => () => {},
  setActiveRoutine: () => {},
  rutinStartErrorDialogOpen: false,
  setRutinStartErrorDialogOpen: () => {},
  hasRutinStartError: false,
  setHasRutinStartError: () => {},
  setCurrentBookingGroup: () => {},
});

export const RutinSettingsContextProvider: React.FC<
  React.PropsWithChildren<IProviderProps>
> = props => {
  const [settings, setSettings] = useState<IRoutineSettings>({});
  const [rutinStartErrorDialogOpen, setRutinStartErrorDialogOpen] =
    useState(false);
  const [hasRutinStartError, setHasRutinStartError] = useState(false);

  const [activeRoutine, setActiveRoutine] = useState<
    IActiveRoutine | undefined
  >(props.activeRoutine);

  const [currentBookingGroup, setCurrentBookingGroup] = useState<
    string | undefined
  >(props.currentBookingGroup);

  const setVisningsTyp = useCallback(
    (rutin: number, collectionName: string) => (typ: TVisningstyp) => {
      const newSettings = clone(settings);
      const info = newSettings[rutin];
      if (info) {
        if (info.visningsTyper) {
          info.visningsTyper[collectionName] = typ;
        } else {
          info.visningsTyper = { [collectionName]: typ };
        }
      } else {
        newSettings[rutin] = { visningsTyper: { [collectionName]: typ } };
      }
      setSettings(newSettings);
    },
    [settings, setSettings]
  );
  const setDetaljurval = useCallback(
    (rutin: number, collectionName: string) =>
      (detaljurval: IUIUrval | null) => {
        const newAvailability = clone(settings);
        const info = newAvailability[rutin];
        if (info) {
          if (info.detaljurval) {
            info.detaljurval[collectionName] = detaljurval ?? undefined;
          } else {
            info.detaljurval = { [collectionName]: detaljurval ?? undefined };
          }
          setSettings(newAvailability);
        }
      },
    [settings, setSettings]
  );
  const updateActiveRoutine = useCallback(
    (routine?: number) => {
      const newActive: IRoutineAvailabilityInfo | undefined = !routine
        ? undefined
        : {
            readonly: false,
            visningsTyper: {},
            detaljurval: {},
            ...settings[routine],
            ...rutinInfos[rutinKeys[routine]],
          };

      setActiveRoutine(
        newActive
          ? {
              routine: newActive,
              predicates:
                newActive && rutinPredicates[newActive.rutinID]
                  ? rutinPredicates[newActive.rutinID]
                  : defaultUrvalFieldsPredicates,
            }
          : undefined
      );
    },
    [settings, setActiveRoutine]
  );
  const value = useMemo(
    () => ({
      settings: settings,
      setVisningsTyp: setVisningsTyp,
      activeRoutine: activeRoutine,
      setActiveRoutine: updateActiveRoutine,
      setDetaljurval: setDetaljurval,
      rutinStartErrorDialogOpen,
      setRutinStartErrorDialogOpen,
      hasRutinStartError,
      setHasRutinStartError,
      setCurrentBookingGroup,
      currentBookingGroup: currentBookingGroup,
    }),
    [
      settings,
      setVisningsTyp,
      activeRoutine,
      updateActiveRoutine,
      setDetaljurval,
      rutinStartErrorDialogOpen,
      hasRutinStartError,
      currentBookingGroup,
    ]
  );
  return (
    <RutinSettingsContext.Provider value={value}>
      {props.children}
    </RutinSettingsContext.Provider>
  );
};

export const RutinSettingsContextConsumer = RutinSettingsContext.Consumer;
